/**
 * Employee account administration (Phase 2). Admin only, enforced on the server.
 *
 * The temporary password from `createUser` and `resetUserPassword` is returned once and stored
 * nowhere readable. The screen shows it until the administrator dismisses it and does not refetch
 * it, because there is nothing to refetch.
 */

import { ApiError } from "./client";
import type {
  CreatedUserAccount,
  Role,
  UserAccount,
  UserAccountList,
  UserStatus,
} from "./types";

const BASE = "/api/admin/users";

async function unwrap<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let detail: unknown;
    try {
      detail = (await response.json())?.detail;
    } catch {
      /* a non-JSON error body; the status carries the meaning */
    }
    throw new ApiError(`Request failed with ${response.status}`, response.status, detail);
  }
  return (await response.json()) as T;
}

function call(path: string, init: RequestInit = {}): Promise<Response> {
  return fetch(`${BASE}${path}`, {
    ...init,
    headers: { Accept: "application/json", ...(init.headers ?? {}) },
    credentials: "include",
  });
}

function json(path: string, method: string, body?: unknown): Promise<Response> {
  return call(path, {
    method,
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

export async function fetchUsers(
  params: {
    role?: Role;
    status?: UserStatus;
    limit?: number;
    offset?: number;
  } = {},
): Promise<UserAccountList> {
  const search = new URLSearchParams();
  if (params.role) search.set("role", params.role);
  if (params.status) search.set("status", params.status);
  // `!== undefined`, not truthiness: offset 0 is the first page and must still be sent, so that a
  // caller moving back to it does not silently inherit whatever the server defaults to.
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.offset !== undefined) search.set("offset", String(params.offset));
  const suffix = search.toString() ? `?${search.toString()}` : "";
  return unwrap<UserAccountList>(await call(suffix));
}

export interface NewUser {
  employee_id: string;
  email: string;
  full_name: string;
  role: Role;
  preferred_language: "ar" | "en";
}

export async function createUser(input: NewUser): Promise<CreatedUserAccount> {
  return unwrap<CreatedUserAccount>(await json("", "POST", input));
}

export async function updateUser(
  userId: string,
  input: { email?: string; full_name?: string; preferred_language?: "ar" | "en" },
): Promise<UserAccount> {
  return unwrap<UserAccount>(
    await json(`/${encodeURIComponent(userId)}`, "PATCH", input),
  );
}

export async function setUserRole(userId: string, role: Role): Promise<UserAccount> {
  return unwrap<UserAccount>(
    await json(`/${encodeURIComponent(userId)}/role`, "POST", { role }),
  );
}

/**
 * Admit a registered account and give it the role it will work under.
 *
 * Approval and role are one call because they are one decision: an approved account with no
 * decided role would be live and unclassified in the window between two clicks. The server refuses
 * anything that is not `pending`, so this cannot be used to change a role or undo a suspension.
 */
export async function approveUser(userId: string, role: Role): Promise<UserAccount> {
  return unwrap<UserAccount>(await json(`/${userId}/approve`, "POST", { role }));
}

export async function suspendUser(userId: string): Promise<UserAccount> {
  return unwrap<UserAccount>(await json(`/${encodeURIComponent(userId)}/suspend`, "POST"));
}

export async function reactivateUser(userId: string): Promise<UserAccount> {
  return unwrap<UserAccount>(
    await json(`/${encodeURIComponent(userId)}/reactivate`, "POST"),
  );
}

export async function resetUserPassword(userId: string): Promise<CreatedUserAccount> {
  return unwrap<CreatedUserAccount>(
    await json(`/${encodeURIComponent(userId)}/reset-password`, "POST"),
  );
}
